import React, { useEffect, useMemo, useState } from 'react';
import IsoDatePicker from './IsoDatePicker';
import RecurringPicker from './RecurringPicker';
import WaitlistOptIn from './WaitlistOptIn';
import { psSlotsLocal } from '../utils/availabilityLocal';
import SlotButton from './SlotButton';

export default function PSInlinePicker({
  offering, onPick, employees: employeesProp = [], live, bookingRange,
  hoursCtx, psSchedule = [], psEmpWork = [], psOffDays = [],
  psScheduleMode = {}, timezone, firstBookableTs,
  psActivePeriods = [], psEquipPools = [], psEquipUsage = [],
  defaultDate = '', winStart = '', winEnd = '', initialParty = 2,
  hideAdd = false, caller = 'unknown',
  onPartyChangeExternal,
  onDraftChange, // optional: emit a draft selection when inputs are valid
}) {
  const [party, setParty] = useState(Number(initialParty || 2));
  const [useMaxEmployees, setUseMaxEmployees] = useState(false);
  const [date, setDate] = useState(defaultDate || '');
  const [loading, setLoading] = useState(false);
  const [empSlots, setEmpSlots] = useState(new Map());
  const [unionTimes, setUnionTimes] = useState([]);
  const [employee, setEmployee] = useState('');
  const [time, setTime] = useState('');
  const [bookableDates, setBookableDates] = useState([]);
  const employees = employeesProp || [];
  const [recurring, setRecurring] = useState({ enabled: false, count: 4, auto_renew: false });
  const [waitlist, setWaitlist] = useState({ earlier: false, specific_times: [] });
  const [adding, setAdding] = useState(false);

  const within = (t, from, to) => {
    if (!t) return false;
    if (from && t < from) return false;
    if (to && t > to) return false;
    return true;
  };

  useEffect(() => {
    let cancelled = false;
    async function computeDates() {
      if (!bookingRange?.start_date || !bookingRange?.end_date) {
        setBookableDates([]);
        return;
      }
      const start = new Date(bookingRange.start_date);
      const end = new Date(bookingRange.end_date);
      const arr = [];
      const employees = employeesProp || [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = d.toISOString().slice(0, 10);
        const computeArgs = {
          dateISO: iso,
          serviceId: offering.id,
          durationMin: offering.duration_min,
          hoursCtx,
          psSchedule,
          empWork: psEmpWork,
          empOff: psOffDays,
          employees,
          tz: timezone,
          firstBookableISO: firstBookableTs,
          live,
          scheduleModeByService: psScheduleMode,
          party,
          activePeriods: psActivePeriods,
          equipPools: psEquipPools,
          equipUsage: psEquipUsage,
        };
        // Log the exact args used for availability computation (computeDates loop)
        const r = psSlotsLocal(computeArgs);
        const counts = new Map();
        (r?.available || []).forEach(grp => {
          (grp.slots || []).forEach(s => {
            if (within(s.start, winStart, winEnd) && within(s.end, winStart, winEnd)) {
              counts.set(s.start, (counts.get(s.start) || 0) + 1);
            }
          });
        });
        const ok = Array.from(counts.values()).some(v => v >= Math.max(1, Number(party || 1)));
        if (ok) arr.push(iso);
        if (arr.length > 365) break;
      }
      if (!cancelled) {
        // avoid unnecessary state updates that trigger re-renders when dates unchanged
        setBookableDates(prev => {
          try {
            const prevStr = (prev || []).join('|');
            const nextStr = (arr || []).join('|');
            if (prevStr === nextStr) return prev;
            return arr;
          } catch (e) {
            return arr;
          }
        });
      }
    }
    computeDates();
    return () => { cancelled = true; };
  }, [
    party,
    offering.id,
    offering.duration_min,
    bookingRange?.start_date,
    bookingRange?.end_date,
    psSchedule,
    psEmpWork,
    psOffDays,
    timezone,
    firstBookableTs,
    live,
    psScheduleMode,
    winStart,
    winEnd,
    // ⬇️ ensure updates when inputs actually change
    (employeesProp || []).map(e => e.id).join('|'),
    (psActivePeriods || []).length,
    (psEquipPools || []).length,
    (psEquipUsage || []).length,
  ]);


  // If parent updates the initialParty prop (e.g. bundle adjusts default), sync into state
  useEffect(() => {
    try {
      const ip = Number(initialParty || 2);
      if (ip !== Number(party || 0)) {
        setParty(ip);
      }
    } catch (e) {}
  }, [initialParty]);

  // Ensure direct user edits to the Party input always log (useful when embedded in bundle)
  function onPartyChange(value) {
    const v = Math.max(1, Number(value || 1));
    setParty(v);
    try { if (typeof onPartyChangeExternal === 'function') onPartyChangeExternal(v); } catch (e) {}
  }

  useEffect(() => {
    if (!bookableDates?.length) {
      if (date) setDate('');
      if (employee) setEmployee('');
      if (time) setTime('');
      setEmpSlots(new Map());
      setUnionTimes([]);
      return;
    }
    if (date && !bookableDates.includes(date)) {
      setDate('');
      setEmployee('');
      setTime('');
      setEmpSlots(new Map());
      setUnionTimes([]);
    }
  }, [bookableDates.join('|')]);

  async function fetchDayAvailability() {
    if (!date) return;
    if (bookableDates.length && !bookableDates.includes(date)) return;
    setLoading(true);
    try {
      const fetchArgs = {
        dateISO: date,
        serviceId: offering.id,
        durationMin: offering.duration_min,
        hoursCtx,
        psSchedule,
        empWork: psEmpWork,
        empOff: psOffDays,
        employees,
        scheduleModeByService: psScheduleMode,
        tz: timezone,
        firstBookableISO: firstBookableTs,
        live,
        activePeriods: psActivePeriods,
        equipPools: psEquipPools,
        equipUsage: psEquipUsage,
      };
      const r = psSlotsLocal(fetchArgs);
      const m = new Map();
      (r?.available || []).forEach(group => {
        const eid = group.employee.id;
        const filtered = (group.slots || []).filter(s =>
          within(s.start, winStart, winEnd) && within(s.end, winStart, winEnd)
        );
        if (filtered.length) m.set(eid, filtered);
      });
  setEmpSlots(m);
      const counts = new Map();
      const allTimes = new Set();
      for (const slots of m.values()) {
        for (const s of slots) {
          allTimes.add(s.start);
          counts.set(s.start, (counts.get(s.start) || 0) + 1);
        }
      }

      const partyReq = Math.max(1, Number(party || 1));
      const union = Array.from(allTimes).sort();
      const filteredUnion = (employee ? union : union.filter(t => (counts.get(t) || 0) >= partyReq))
        .filter(t => within(t, winStart, winEnd));
      setUnionTimes(filteredUnion);

      if (employee && !m.has(employee)) setEmployee('');
      if (time && !filteredUnion.includes(time)) setTime('');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setEmployee('');
    setTime('');
    setEmpSlots(new Map());
    setUnionTimes([]);
    if (date) fetchDayAvailability();
  }, [date]);

  // NEW: When live availability or scheduling context changes, refresh times for the selected date
  useEffect(() => {
    if (!date) return;
    // Re-fetch current day to reflect live changes (e.g., cancellations/holds)
    fetchDayAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Availability-affecting deps
    JSON.stringify(live),
    party,
    winStart,
    winEnd,
    offering.id,
    offering.duration_min,
    timezone,
    firstBookableTs,
    // Schedules and constraints
    JSON.stringify(psSchedule),
    JSON.stringify(psEmpWork),
    JSON.stringify(psOffDays),
    JSON.stringify(psScheduleMode),
    JSON.stringify(psActivePeriods),
    JSON.stringify(psEquipPools),
    JSON.stringify(psEquipUsage),
    JSON.stringify(hoursCtx),
  ]);
  useEffect(() => {
    if (date && !waitlist.date) {
      setWaitlist(w => ({ ...w, date }));
    }
  }, [date]);

  const employeesWithSlots = useMemo(() => {
    if (time) {
      return employees.filter(e => (empSlots.get(e.id) || []).some(s => s.start === time));
    }
    return employees.filter(e => (empSlots.get(e.id) || []).length > 0);
  }, [employees, empSlots, time]);

  const timesForSelection = useMemo(() => {
    if (employee) return Array.from(new Set((empSlots.get(employee) || []).map(s => s.start))).sort();
    return unionTimes;
  }, [employee, empSlots, unionTimes]);

  const unavailableTimes = React.useMemo(() => {
    const counts = new Map();
    for (const slots of empSlots.values()) {
      for (const s of slots || []) {
        counts.set(s.start, (counts.get(s.start) || 0) + 1);
      }
    }
    const all = Array.from(counts.keys()).sort();
    const need = Math.max(1, Number(party || 1));
    return all.filter(t => (counts.get(t) || 0) < need).filter(t => within(t, winStart, winEnd));
  }, [empSlots, party]);

  async function addSelection() {
    if (!date) return;
    setAdding(true);
    if (employee && time) {
      const s = (empSlots.get(employee) || []).find(x => x.start === time);
      if (!s) return;
      try {
        await onPick(offering, {
          slot_start: `${date}T${s.start}:00`,
          slot_end: `${date}T${s.end}:00`,
          employee_id: employee,
          party,
          guests_json: {
            recurring: recurring.enabled
              ? { enabled: true, cadence: 'weekly', count: Number(recurring.count || 2), auto_renew: !!recurring.auto_renew }
              : null,
            waitlist: (waitlist.earlier || (waitlist.specific_times?.length > 0))
              ? {
                enabled: true,
                earlier: !!waitlist.earlier,
                specific_times: (waitlist.specific_times || []).filter(Boolean).slice(0, 3),
                date: waitlist.date || null
              }
              : null,
            date_scope: waitlist.date || date
          }

        });
      } finally { setAdding(false); }
      return;
    }
    if (!employee && time) {
      const pool = employeesWithSlots;
      if (!pool.length) return;
      if (useMaxEmployees) {
        const chosen = pool.slice(0, Math.max(1, party));
        for (const e of chosen) {
          const s = (empSlots.get(e.id) || []).find(x => x.start === time);
          if (!s) continue;
          try {
            await onPick(offering, {
              slot_start: `${date}T${s.start}:00`,
              slot_end: `${date}T${s.end}:00`,
              employee_id: e.id,
              party: 1,
              guests_json: {
                recurring: recurring.enabled
                  ? { enabled: true, cadence: 'weekly', count: Number(recurring.count || 2), auto_renew: !!recurring.auto_renew }
                  : null,
                waitlist: (waitlist.earlier || (waitlist.specific_times?.length > 0))
                  ? { enabled: true, earlier: !!waitlist.earlier, specific_times: (waitlist.specific_times || []).filter(Boolean).slice(0, 3) }
                  : null,
                date_scope: date
              }

            });
          } finally { setAdding(false); }
        }
      } else {
        const e = pool[0];
        const s = (empSlots.get(e.id) || []).find(x => x.start === time);
        if (!s) return;
        try {
          await onPick(offering, {
            slot_start: `${date}T${s.start}:00`,
            slot_end: `${date}T${s.end}:00`,
            employee_id: e.id,
            party,
            guests_json: {
              recurring: recurring.enabled
                ? { enabled: true, cadence: 'weekly', count: Number(recurring.count || 2), auto_renew: !!recurring.auto_renew }
                : null,
              waitlist: (waitlist.earlier || (waitlist.specific_times?.length > 0))
                ? { enabled: true, earlier: !!waitlist.earlier, specific_times: (waitlist.specific_times || []).filter(Boolean).slice(0, 3) }
                : null,
              date_scope: date
            }

          });
        } finally { setAdding(false); }
      }
    }
  }

  useEffect(() => {
    if (!empSlots || empSlots.size === 0) return;
    const counts = new Map();
    const allTimes = new Set();
    for (const slots of empSlots.values()) {
      for (const s of slots) {
        allTimes.add(s.start);
        counts.set(s.start, (counts.get(s.start) || 0) + 1);
      }
    }

    const partyReq = Math.max(1, Number(party || 1));
    const union = Array.from(allTimes).sort();

    const filteredUnion = employee
      ? union
      : union.filter(t => (counts.get(t) || 0) >= partyReq);

    setUnionTimes(filteredUnion);

    if (time && !filteredUnion.includes(time)) setTime('');
  }, [party, employee, empSlots]);

  // Emit a draft selection to parent when the form is in a valid state (mirrors Add enablement)
  useEffect(() => {
    if (typeof onDraftChange !== 'function') return;
    // Valid when a date is selected, we have a time, and there are bookable dates (not loading)
    const ready = !!date && !!time && !!(bookableDates?.length) && !loading;
    if (!ready) {
      try { onDraftChange(offering, null); } catch (e) {}
      return;
    }
    // Determine which employee/slot to use
    let chosenEmpId = employee || '';
    let chosenSlot = null;
    if (chosenEmpId) {
      chosenSlot = (empSlots.get(chosenEmpId) || []).find(s => s.start === time) || null;
    } else {
      // pick first available employee at that time
      for (const [eid, slots] of empSlots.entries()) {
        const s = (slots || []).find(x => x.start === time);
        if (s) { chosenEmpId = eid; chosenSlot = s; break; }
      }
    }
    if (!chosenSlot || !chosenEmpId) {
      try { onDraftChange(offering, null); } catch (e) {}
      return;
    }
    const draft = {
      slot_start: `${date}T${chosenSlot.start}:00`,
      slot_end: `${date}T${chosenSlot.end}:00`,
      employee_id: chosenEmpId,
      party: Math.max(1, Number(party || 1)),
    };
    try { onDraftChange(offering, draft); } catch (e) {}
  }, [onDraftChange, offering, date, time, employee, party, bookableDates?.length, loading, empSlots]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-600">Date</label>
          <IsoDatePicker
            value={date}
            onChange={setDate}
            minDate={bookingRange?.start_date}
            maxDate={bookingRange?.end_date}
            includeDates={bookableDates}
            includeDatesStrict
            isClearable
          />
          {!bookableDates?.length && (
            <div className="text-xs text-amber-700 mt-1">No bookable dates are available yet.</div>
          )}
        </div>
        <div>
          <label className="block text-xs text-gray-600">Party</label>
          <input type="number" min={1} value={party}
            onChange={e => onPartyChange(e.target.value)}
            className="border rounded px-2 py-1 w-20" />
        </div>
        <div>
          <label className="block text-xs text-gray-600">Employee</label>
          <select value={employee} onChange={e => setEmployee(e.target.value)}
            className="border rounded px-2 py-1 w-44"
            disabled={!date || loading || !employeesWithSlots.length || !bookableDates.length}>
            <option value="">Any</option>
            {employeesWithSlots.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600">Time</label>
          <select value={time} onChange={e => setTime(e.target.value)}
            className="border rounded px-2 py-1 w-32"
            disabled={!date || loading || !timesForSelection.length || !bookableDates.length}>
            <option value="">Any</option>
            {timesForSelection.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
          <div className="flex items-center gap-2 ml-auto">
          {!hideAdd && (
            <button
              disabled={!date || loading || !time || !bookableDates.length || adding}
              className="px-3 py-2 rounded bg-black text-white text-sm disabled:opacity-40"
              onClick={addSelection}
            >
              {adding ? 'Adding…' : 'Add'}
            </button>
          )}
        </div>
      </div>
      {loading && <div className="text-xs text-gray-500">Checking…</div>}
      <div className="grid gap-3">
        <RecurringPicker value={recurring} onChange={setRecurring} />
        <WaitlistOptIn
          value={waitlist}
          onChange={setWaitlist}
          unavailableTimes={unavailableTimes}
          minDate={bookingRange?.start_date}
          maxDate={bookingRange?.end_date}
          includeDates={bookableDates}
        />
      </div>
    </div>
  );
}
